import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { CheckSquare, Clock, AlertTriangle, FolderOpen, TrendingUp, User, Activity, Plus } from 'lucide-react';

const PRIORITY_COLORS = { kritik: '#ef4444', yuksek: '#f97316', normal: '#007aff', dusuk: '#9ca3af' };
const PRIORITY_LABELS = { kritik: 'Kritik', yuksek: 'Yüksek', normal: 'Normal', dusuk: 'Düşük' };
const STATUS_COLORS = { yapilacak: '#6b7280', 'devam-ediyor': '#007aff', incelemede: '#ff9500', tamamlandi: '#34c759' };
const STATUS_LABELS = { yapilacak: 'Yapılacak', 'devam-ediyor': 'Devam Ediyor', incelemede: 'İncelemede', tamamlandi: 'Tamamlandı' };
const ACTION_ICONS = { create: '✨', status: '🔄', assign: '👤', comment: '💬', file: '📎' };

export default function AdminTaskDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tasks/stats').then(r => { setStats(r.data); setLoading(false); });
  }, []);

  if (loading) return <AdminLayout><div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Yükleniyor...</div></AdminLayout>;

  const { tasks, projects, recent_activities, my_tasks } = stats;
  const completionRate = tasks.total > 0 ? Math.round((tasks.completed / tasks.total) * 100) : 0;

  const statCards = [
    { icon: CheckSquare, label: 'Toplam Görev', value: tasks.total, color: '#007aff', bg: 'rgba(0,122,255,0.08)' },
    { icon: Clock, label: 'Devam Eden', value: parseInt(tasks.inprogress), color: '#f97316', bg: 'rgba(249,115,22,0.08)' },
    { icon: AlertTriangle, label: 'Geciken', value: parseInt(tasks.overdue), color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
    { icon: FolderOpen, label: 'Aktif Proje', value: parseInt(projects.active), color: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },
    { icon: TrendingUp, label: 'Tamamlanan', value: parseInt(tasks.completed), color: '#34c759', bg: 'rgba(52,199,89,0.08)' },
    { icon: AlertTriangle, label: 'Kritik', value: parseInt(tasks.critical), color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  ];

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckSquare size={22} color="#007aff" /> Görev Yönetimi
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/yonetim/gorevler/kanban')}
            style={{ background: 'rgba(0,122,255,0.08)', color: '#007aff', border: 'none', borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            📋 Kanban
          </button>
          <button onClick={() => navigate('/yonetim/gorevler/liste')}
            style={{ background: 'rgba(0,122,255,0.08)', color: '#007aff', border: 'none', borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            📝 Liste
          </button>
          <button onClick={() => navigate('/yonetim/gorevler/projeler')}
            style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Proje
          </button>
        </div>
      </div>

      {/* İstatistik kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {statCards.map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} style={{ background: bg, borderRadius: 14, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12, border: '1px solid rgba(255,255,255,0.8)' }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={18} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tamamlanma oranı */}
      <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>Genel Tamamlanma Oranı</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: completionRate >= 70 ? '#34c759' : completionRate >= 40 ? '#f97316' : '#ef4444' }}>{completionRate}%</div>
        </div>
        <div style={{ height: 10, background: '#f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: completionRate + '%', background: completionRate >= 70 ? '#34c759' : completionRate >= 40 ? '#f97316' : '#ef4444', borderRadius: 6, transition: 'width 0.8s' }} />
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: STATUS_COLORS[key] }} />
              <span style={{ fontSize: 12, color: '#6b7280' }}>{label}: <strong>{tasks[key === 'yapilacak' ? 'todo' : key === 'devam-ediyor' ? 'inprogress' : key === 'incelemede' ? 'review' : 'completed'] || 0}</strong></span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Bana atanan görevler */}
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
            <User size={15} color="#007aff" /> Bana Atanan Görevler
          </div>
          {my_tasks.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Atanmış görev yok</div>
          ) : my_tasks.map(t => (
            <div key={t.id} onClick={() => navigate('/yonetim/gorevler/liste')}
              style={{ padding: '11px 18px', borderBottom: '1px solid rgba(0,0,0,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,122,255,0.03)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: PRIORITY_COLORS[t.priority], flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.project_name}</div>
              </div>
              {t.due_date && (
                <div style={{ fontSize: 10, color: new Date(t.due_date) < new Date() ? '#ef4444' : '#9ca3af', flexShrink: 0 }}>
                  {new Date(t.due_date).toLocaleDateString('tr-TR')}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Son aktiviteler */}
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
            <Activity size={15} color="#007aff" /> Son Aktiviteler
          </div>
          {recent_activities.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Aktivite yok</div>
          ) : recent_activities.map((a, i) => (
            <div key={i} style={{ padding: '10px 18px', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{ACTION_ICONS[a.action] || '📌'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                  <strong>{a.user_name}</strong> — {a.task_title}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{a.description}</div>
              </div>
              <div style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>
                {new Date(a.created_at).toLocaleDateString('tr-TR')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
